import styled, { css } from 'styled-components'
import { tabletBreakpoint } from 'react-components'
import Modal from '../../../common/modal'
import ChevronDownIcon from '../../../../assets/icons/chevron-down.svg'
import CheckIcon from '../../../../assets/icons/check-icon.svg'
import CloseIcon from '../../../../assets/icons/close.svg'
import StarIcon from '../../../../assets/icons/star.svg'
import PlusIcon from '../../../../assets/icons/plus.svg'

const OFFER_COLOR = '#43756a'
const NEGATIVE_COLOR = '#9d5764'
const COLUMN_WIDTH = '12rem'
const MOBILE_MAX_WIDTH = (tabletBreakpoint.min ?? 768) - 1

// The shared Modal's own `.Modal` box (not `ModalContainer` below) is what's actually visible — it has
// its own background and border-radius, and `ModalContainer` sits flush inside it with no inset. Override
// it here, scoped to this modal only, rather than changing the shared component's radius for every
// consumer site-wide.
export const CompareModalShell = styled(Modal)(
    ({ theme }) => css`
        & .Modal {
            border-radius: ${theme.typography.border.radius.xxLarge};
        }
    `,
)

export const ModalContainer = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        width: min(64rem, 94vw);
        max-height: 88vh;
        background-color: ${theme.colors.white};
        border-radius: ${theme.typography.border.radius.xxLarge};
        overflow: hidden;
    `,
)

export const Header = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing.small};
        padding: ${theme.spacing.medium} ${theme.spacing.large};
        width: 100%;
        box-sizing: border-box;
    `,
)

export const HeaderTitle = styled.h2(
    ({ theme }) => css`
        margin: 0;
        min-width: 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: 1.375rem;
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.75rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const CloseButton = styled.button(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: center;
        padding: ${theme.spacing.xxSmall};
        border: none;
        background: transparent;
        cursor: pointer;
        flex-shrink: 0;
    `,
)

export const ChevronDown = styled(ChevronDownIcon)(
    ({ theme }) => css`
        width: 1.5rem;
        height: 1.5rem;
        path {
            fill: ${theme.colors.tfwBlack99};
        }
    `,
)

export const Body = styled.div(
    ({ theme }) => css`
        flex: 1;
        min-height: 0;
        overflow: auto;
        padding: 0 ${theme.spacing.large} ${theme.spacing.large};
    `,
)

export const Row = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: flex-start;
        gap: ${theme.spacing.medium};
        min-width: min-content;
    `,
)

export const CardsBox = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: stretch;
        border: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwDefaultInputBorder};
        border-radius: ${theme.typography.border.radius.medium};
        overflow: hidden;
        flex-shrink: 0;
    `,
)

export const Column = styled.div(
    ({ theme }) => css`
        display: flex;
        flex-direction: column;
        flex: 0 0 ${COLUMN_WIDTH};
        width: ${COLUMN_WIDTH};
        border-right: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwDefaultInputBorder};

        &:last-child {
            border-right: none;
        }
    `,
)

export const ImageBox = styled.div(
    ({ theme }) => css`
        position: relative;
        width: 100%;
        padding: ${theme.spacing.xSmall} ${theme.spacing.xSmall} 0;
        flex-shrink: 0;
        cursor: pointer;
    `,
)

export const ImageCarouselClip = styled.div(
    ({ theme }) => css`
        border-radius: ${theme.typography.border.radius.xLarge};
        overflow: hidden;
    `,
)

export const HeartOverlay = styled.div(
    ({ theme }) => css`
        position: absolute;
        top: ${theme.spacing.small};
        right: ${theme.spacing.small};
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background: radial-gradient(100% 100% at 100% 0%, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
        display: flex;
        align-items: center;
        justify-content: center;
    `,
)

export const InfoBlock = styled.div(
    ({ theme }) => css`
        padding: ${theme.spacing.xSmall} ${theme.spacing.medium};
    `,
)

export const Region = styled.p(
    ({ theme }) => css`
        margin: 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        line-height: 1rem;
        color: ${theme.colors.tfwBlack45};
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    `,
)

export const Title = styled.p(
    ({ theme }) => css`
        margin: ${theme.spacing.xxSmall} 0 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.375rem;
        color: ${theme.colors.tfwBlack99};
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        min-height: 2.75rem;
    `,
)

export const Section = styled.div<{ $minHeight?: string }>(
    ({ theme, $minHeight }) => css`
        padding: ${theme.spacing.small} ${theme.spacing.medium};
        border-top: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwDefaultInputBorder};
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 0.375rem;
        ${$minHeight && `min-height: ${$minHeight};`}
    `,
)

export const StatLine = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const RatingLine = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xxSmall};
        min-height: 1.375rem;
    `,
)

export const StyledStar = styled(StarIcon)`
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
`

export const RatingValue = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        color: ${theme.colors.tfwBlack99};
    `,
)

export const RatingCount = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack45};
    `,
)

export const NoRating = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack45};
    `,
)

export const PriceMain = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.large};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const PriceSub = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xSmall};
        min-height: 1rem;
    `,
)

export const PriceStrike = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack45};
        text-decoration: line-through;
    `,
)

export const Discount = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        color: ${OFFER_COLOR};
    `,
)

export const InfoRow = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xSmall};
    `,
)

export const InfoText = styled.span<{ $muted?: boolean; $positive?: boolean }>(
    ({ theme, $muted, $positive }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
        color: ${$positive ? OFFER_COLOR : $muted ? theme.colors.tfwBlack45 : theme.colors.tfwBlack99};
    `,
)

export const CheckMark = styled(CheckIcon)<{ $positive?: boolean }>(
    ({ theme, $positive }) => css`
        width: 1rem;
        height: 1rem;
        flex-shrink: 0;
        path {
            stroke: ${$positive ? OFFER_COLOR : theme.colors.tfwBlack99};
        }
    `,
)

export const CrossMark = styled(CloseIcon)(
    ({ theme }) => css`
        width: 0.875rem;
        height: 0.875rem;
        flex-shrink: 0;
        path {
            fill: ${theme.colors.tfwBlack45};
        }
    `,
)

export const RemoveLink = styled.button(
    ({ theme }) => css`
        align-self: flex-start;
        margin: ${theme.spacing.xxSmall} ${theme.spacing.medium} ${theme.spacing.small};
        padding: 0;
        border: none;
        background: transparent;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
        color: ${NEGATIVE_COLOR};
        text-decoration: underline;
        text-decoration-style: dotted;
        cursor: pointer;
    `,
)

export const AddMorePanel = styled.div(
    ({ theme }) => css`
        display: flex;
        flex: 1 1 0;
        min-width: 0;
        flex-direction: column;
        align-items: center;
        align-self: center;
        justify-content: center;
        gap: ${theme.spacing.xSmall};
        padding: ${theme.spacing.medium} ${theme.spacing.xSmall};
        text-align: center;

        @media only screen and (max-width: ${MOBILE_MAX_WIDTH}px) {
            flex: 0 0 auto;
            width: 16rem;
            min-width: 16rem;
            padding: ${theme.spacing.xLarge} ${theme.spacing.medium};
        }
    `,
)

export const PlusAvatar = styled.button(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: center;
        width: 2.5rem;
        height: 2.5rem;
        padding: 0;
        border: none;
        border-radius: 50%;
        background-color: ${theme.colors.tfwGrey};
        cursor: pointer;
    `,
)

export const StyledPlus = styled(PlusIcon)`
    width: 1.5rem;
    height: 1.5rem;
`

export const AddMoreTitle = styled.p(
    ({ theme }) => css`
        margin: ${theme.spacing.xxSmall} 0 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.medium};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        line-height: 1.5rem;
        color: ${theme.colors.tfwBlack99};
    `,
)

export const AddMoreSubtitle = styled.p(
    ({ theme }) => css`
        margin: 0;
        max-width: 18rem;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        line-height: 1.375rem;
        color: ${theme.colors.tfwBlack45};
    `,
)

export const BrowseButton = styled.button(
    ({ theme }) => css`
        margin-top: ${theme.spacing.xSmall};
        max-width: 100%;
        padding: ${theme.spacing.xSmall} ${theme.spacing.large};
        border: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwBlack99};
        border-radius: ${theme.typography.border.radius.medium};
        background-color: ${theme.colors.white};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.highlight};
        color: ${theme.colors.tfwBlack99};
        cursor: pointer;
    `,
)

export const SavedText = styled.p(
    ({ theme }) => css`
        margin: ${theme.spacing.xSmall} 0 0;
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.xSmall};
        line-height: 1rem;
        color: ${theme.colors.tfwBlack45};
    `,
)

export const Footer = styled.div(
    ({ theme }) => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: ${theme.spacing.medium};
        padding: ${theme.spacing.medium} ${theme.spacing.large};
        border-top: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwDefaultInputBorder};
        flex-shrink: 0;
    `,
)

export const SelectedCount = styled.span(
    ({ theme }) => css`
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        color: ${theme.colors.tfwBlack45};
    `,
)

export const SendButton = styled.button(
    ({ theme }) => css`
        flex-shrink: 0;
        min-width: 4.5rem;
        padding: ${theme.spacing.small} ${theme.spacing.xLarge};
        border: none;
        border-radius: ${theme.typography.border.radius.xLarge};
        background-color: ${theme.colors.tfwYellow};
        font-family: ${theme.typography.font.family.lato};
        font-size: ${theme.typography.font.size.small};
        font-weight: ${theme.typography.font.lato.weight.semiBold};
        color: ${theme.colors.tfwBlack99};
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: ${theme.spacing.xxSmall};

        &:hover {
            background-color: ${theme.colors.tfwYellowHover};
        }
    `,
)

export const MobileBody = styled.div(
    ({ theme }) => css`
        overflow: auto;
        padding: 0 ${theme.spacing.medium} ${theme.spacing.medium};
    `,
)

export const MobileFooter = styled.div(
    ({ theme }) => css`
        width: 100%;
        padding: ${theme.spacing.small} ${theme.spacing.medium};
    `,
)

export const MobileSendButton = styled(SendButton)`
    width: 100%;
    border-radius: 0.75rem;
`
